import { useEffect } from 'react';
import type { ReactNode, RefObject } from 'react';
import { cn } from '@/lib/utils';
import {
  ArtifactStoreContext,
  ArtifactCallbacksContext,
  type ArtifactCallbacks,
} from '@/artifact/store';
import type { ArtifactStore } from '@/artifact/store';
import { RenderNode } from '@/artifact/RenderNode';
import type { ArtifactFocusTarget } from '@/hooks/useArtifactEngine';

interface ArtifactContentProps {
  store: ArtifactStore;
  callbacks: ArtifactCallbacks;
  rootCuids: number[];
  containerRef: RefObject<HTMLDivElement>;
  focusTarget?: ArtifactFocusTarget | null;
  className?: string;
  surfaceClassName?: string;
  overlay?: ReactNode;
  emptyState: ReactNode;
  forceOverlay?: boolean;
}

export const ArtifactContent = (props: ArtifactContentProps) => {
  useEffect(() => {
    if (!props.focusTarget) return;

    const container = props.containerRef.current;
    const target = container?.querySelector<HTMLElement>(
      `.a-node[data-id="${props.focusTarget.cuid}"]`,
    );
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    target.classList.remove('a-node-focus-pulse');
    void target.offsetWidth;
    target.classList.add('a-node-focus-pulse');
  }, [props.containerRef, props.focusTarget]);

  return (
    <div className={cn('a-theme-root relative flex-1 overflow-y-auto', props.className)} ref={props.containerRef}>
      {props.rootCuids.length > 0 ? (
        <ArtifactStoreContext.Provider value={props.store}>
          <ArtifactCallbacksContext.Provider value={props.callbacks}>
            <div className="relative min-h-full">
              <div className={cn('a-surface', props.surfaceClassName)}>
                {props.rootCuids.map((cuid) => (
                  <RenderNode key={cuid} cuid={cuid} />
                ))}
              </div>
              {props.overlay}
            </div>
          </ArtifactCallbacksContext.Provider>
        </ArtifactStoreContext.Provider>
      ) : (
        <ArtifactStoreContext.Provider value={props.store}>
          <ArtifactCallbacksContext.Provider value={props.callbacks}>
            <div className="relative h-full">
              {props.emptyState}
              {props.forceOverlay && props.overlay}
            </div>
          </ArtifactCallbacksContext.Provider>
        </ArtifactStoreContext.Provider>
      )}
    </div>
  );
};
